/**
 * Fase 4.1 — Verlof (management) volledig herwerkt.
 * Tabs: Openstaand / Goedgekeurd / Afgewezen / Historie.
 * Rustige statuskleuren (geen glow), overzichtelijke rijen met medewerker,
 * type, periode, status en (indien beschikbaar) locatie + team.
 * RBAC blijft server-side leidend — deze pagina hangt aan /api/leave-requests
 * dat manager/teamlead automatisch scope-t via managed_location_ids.
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { displayEmployeeName } from "@/lib/employeeName";
import { Check, X as XIcon, Clock, CheckCircle2, XCircle, Archive, Calendar, MapPin, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Fase 6.5A — administratieve notities bij ziekmeldingen zijn hard beperkt
// tot 250 tekens (server-side canonical). De UI dwingt hetzelfde af én
// waarschuwt expliciet dat medische inhoud niet in dit veld thuishoort.
const SICK_NOTE_MAX = 250;

const TABS = (t) => [
  { id: "pending",  Icon: Clock,         label: t("lr_tab_pending"),  empty: t("lr_empty_pending") },
  { id: "approved", Icon: CheckCircle2,  label: t("lr_tab_approved"), empty: t("lr_empty_approved") },
  { id: "rejected", Icon: XCircle,       label: t("lr_tab_rejected"), empty: t("lr_empty_rejected") },
  { id: "history",  Icon: Archive,       label: t("lr_tab_history"),  empty: t("lr_empty_history") },
];

// Rustige statuskleuren (geen felle glow). Historie krijgt neutraal grijs.
function statusStyle(s) {
  switch (s) {
    case "pending":  return { bg: "hsl(38 92% 94%)",  fg: "hsl(28 82% 40%)",  ring: "hsl(38 92% 78%)" };
    case "approved": return { bg: "hsl(150 55% 92%)", fg: "hsl(150 55% 30%)", ring: "hsl(150 55% 78%)" };
    case "rejected": return { bg: "hsl(0 65% 95%)",   fg: "hsl(0 65% 42%)",   ring: "hsl(0 65% 82%)" };
    default:         return { bg: "hsl(220 12% 94%)", fg: "hsl(220 12% 38%)", ring: "hsl(220 12% 82%)" };
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("nl", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function daysInclusive(a, b) {
  if (!a || !b) return 0;
  const ms = new Date(b + "T00:00:00") - new Date(a + "T00:00:00");
  return Math.max(1, Math.round(ms / 86400000) + 1);
}

export default function LeaveRequests() {
  const { t } = useI18n();
  const [tab, setTab] = useState("pending");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fase 6.5A — decide-modal (verplicht voor sick, optioneel voor overige).
  const [decideModal, setDecideModal] = useState(null); // { row, decision } | null
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      // Historie = alles; overige tabs filteren client-side per status.
      setList((await api.get("/leave-requests")).data || []);
    } catch (e) {
      setError(e.response?.data?.detail || t("error"));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (tab === "history") return list;
    return list.filter((r) => r.status === tab);
  }, [list, tab]);

  const counts = useMemo(() => ({
    pending:  list.filter((r) => r.status === "pending").length,
    approved: list.filter((r) => r.status === "approved").length,
    rejected: list.filter((r) => r.status === "rejected").length,
    history:  list.length,
  }), [list]);

  const startDecide = (row, decision) => {
    setDecideModal({ row, decision });
    setNote("");
  };

  const submitDecide = async () => {
    if (!decideModal) return;
    const { row, decision } = decideModal;
    // Client-side guard mirrors the server 250-cap so we fail fast on sick.
    if (row.leave_type === "sick" && note.length > SICK_NOTE_MAX) {
      toast.error(t("error"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { status: decision };
      const trimmed = note.trim();
      if (trimmed) payload.manager_note = trimmed;
      await api.post(`/leave-requests/${row._id}/decide`, payload);
      toast.success(t(decision === "approved" ? "lr_action_approve" : "lr_action_reject"));
      setDecideModal(null);
      setNote("");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "sick_manager_note_too_long") {
        toast.error(t("lr_sick_note_help"));
      } else {
        toast.error((typeof detail === "string" ? detail : null) || t("error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-requests" className="max-w-5xl">
      <PageHeader
        eyebrow={t("leave_requests") || t("lr_page_title")}
        title={t("lr_page_title")}
        subtitle={t("lr_page_subtitle")}
      />

      {/* Tabs (segments) */}
      <div className="card-glass p-1 grid grid-cols-4 gap-1 w-full mb-4" data-testid="lr-tabs">
        {TABS(t).map((tt) => {
          const active = tab === tt.id;
          return (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              data-testid={`lr-tab-${tt.id}`}
              className={`relative flex items-center justify-center gap-2 h-11 rounded-full text-[13px] font-semibold transition
                ${active
                  ? "bg-[hsl(var(--primary))] text-white shadow-[var(--shadow-sm)]"
                  : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] hover:bg-[hsl(var(--muted))]"}`}
            >
              <tt.Icon size={15} strokeWidth={2} />
              <span className="hidden sm:inline">{tt.label}</span>
              {counts[tt.id] > 0 && (
                <span className={`min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-bold tabular-nums leading-none
                  ${active ? "bg-white text-[hsl(var(--primary))]" : "bg-[hsl(var(--primary))] text-white"}`}>
                  {counts[tt.id]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="card p-6 text-center text-[hsl(var(--text-muted))] text-sm" data-testid="lr-loading">
          {t("rep_loading")}
        </div>
      )}
      {error && !loading && (
        <div className="card p-6 text-center text-[hsl(var(--danger))] text-sm" data-testid="lr-error">
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div data-testid="lr-empty">
          <EmptyState type="sent" title={TABS(t).find((x) => x.id === tab)?.empty} />
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="lr-list">
          <AnimatePresence>
            {filtered.map((r) => (
              <LeaveRow key={r._id} r={r} onDecide={startDecide} />
            ))}
          </AnimatePresence>
        </div>
      )}

      <DecideModal
        modal={decideModal}
        note={note}
        setNote={setNote}
        submitting={submitting}
        onCancel={() => { setDecideModal(null); setNote(""); }}
        onConfirm={submitDecide}
      />
    </div>
  );
}


/**
 * Fase 6.5A — decide-modal. Voor sick leaves is dit veld bewust een
 * "Administratieve opmerking" met een harde 250-char cap + expliciete
 * waarschuwing dat medische inhoud er niet in thuishoort. Voor overige
 * verloftypes is de notitie optioneel en heeft geen sick-cap (er geldt
 * alleen de generieke 2000-char safety-cap uit LeaveDecision).
 */
function DecideModal({ modal, note, setNote, submitting, onCancel, onConfirm }) {
  const { t } = useI18n();
  if (!modal) return null;
  const { row, decision } = modal;
  const isSick = row.leave_type === "sick";
  const titleKey = isSick
    ? (decision === "approved" ? "lr_modal_title_approve_sick" : "lr_modal_title_reject_sick")
    : (decision === "approved" ? "lr_modal_title_approve" : "lr_modal_title_reject");
  const over = isSick && note.length > SICK_NOTE_MAX;
  const counter = t("lr_sick_note_counter").replace("{count}", String(note.length));
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4"
        onClick={onCancel}
        data-testid="lr-decide-overlay"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="card p-6 md:p-7 w-full max-w-md"
          data-testid={isSick ? "lr-decide-modal-sick" : "lr-decide-modal"}
        >
          <div className="eyebrow mb-2">{t(row.leave_type) || row.leave_type}</div>
          <div className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))] mb-4" data-testid="lr-decide-title">
            {t(titleKey)}
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11.5px] font-semibold text-[hsl(var(--text))] uppercase tracking-[0.08em]">
              {isSick ? t("lr_sick_note_label") : t("lr_note_optional_label")}
            </span>
            {isSick && (
              <div
                data-testid="lr-sick-note-help"
                className="flex items-start gap-2 text-[12px] text-[hsl(var(--warn))] dark:text-[hsl(38_92%_75%)] leading-relaxed rounded-xl border hairline bg-[hsl(38_92%_96%)] dark:bg-[hsl(38_45%_14%)] px-3 py-2.5 mb-1"
              >
                <AlertTriangle size={13} strokeWidth={2} className="mt-0.5 shrink-0" />
                <span>{t("lr_sick_note_help")}</span>
              </div>
            )}
            <textarea
              rows={isSick ? 3 : 4}
              value={note}
              onChange={(e) => setNote(isSick ? e.target.value.slice(0, SICK_NOTE_MAX) : e.target.value)}
              maxLength={isSick ? SICK_NOTE_MAX : undefined}
              placeholder={isSick ? t("lr_sick_note_optional_placeholder") : ""}
              className="input-field !h-auto !py-3 leading-relaxed resize-none"
              data-testid={isSick ? "lr-sick-note-textarea" : "lr-note-textarea"}
            />
            {isSick && (
              <span
                className={`self-end text-[11px] tabular-nums font-medium ${over ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--text-muted))]"}`}
                data-testid="lr-sick-note-counter"
                aria-live="polite"
              >
                {counter}
              </span>
            )}
          </label>

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={onCancel}
              className="btn btn-ghost"
              data-testid="lr-decide-cancel"
              type="button"
            >{t("lr_confirm_cancel")}</button>
            <button
              onClick={onConfirm}
              disabled={submitting || over}
              className={`btn ${decision === "approved" ? "btn-primary" : "btn-ghost"}`}
              data-testid="lr-decide-confirm"
              type="button"
            >
              {decision === "approved" ? <Check size={13} strokeWidth={2.4} /> : <XIcon size={13} strokeWidth={2.4} />}
              {decision === "approved" ? t("lr_confirm_approve") : t("lr_confirm_reject")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function LeaveRow({ r, onDecide }) {
  const { t } = useI18n();
  const s = statusStyle(r.status);
  const d = daysInclusive(r.start_date, r.end_date);
  const canDecide = r.status === "pending";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="card p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
      data-testid={`leave-${r._id}`}
    >
      {/* Meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] truncate" data-testid={`leave-emp-${r._id}`}>
            {displayEmployeeName(r, t)}
          </span>
          <span
            className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-semibold ring-1"
            style={{ background: s.bg, color: s.fg, borderColor: s.ring, boxShadow: "none" }}
            data-testid={`leave-status-${r._id}`}
          >
            {t(`status_${r.status}`) || t(r.status)}
          </span>
          <span className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]">
            {t(r.leave_type) || r.leave_type}
          </span>
        </div>
        <div className="mt-2 text-[13px] text-[hsl(var(--text-muted))] font-medium flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={12} strokeWidth={2} />
            <span className="tabular-nums">{fmtDate(r.start_date)}</span>
            <span>→</span>
            <span className="tabular-nums">{fmtDate(r.end_date)}</span>
            <span className="text-[hsl(var(--text-strong))] font-bold ml-1">· {d}d</span>
          </span>
          {r.location_name && (
            <span className="inline-flex items-center gap-1.5" data-testid={`leave-loc-${r._id}`}>
              <MapPin size={12} strokeWidth={2} /> {r.location_name}
            </span>
          )}
          {r.team_name && (
            <span className="inline-flex items-center gap-1.5" data-testid={`leave-team-${r._id}`}>
              <Building2 size={12} strokeWidth={2} /> {r.team_name}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      {canDecide && (
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onDecide(r, "approved")}
            className="btn btn-primary !h-9"
            data-testid={`approve-leave-${r._id}`}
          >
            <Check size={13} strokeWidth={2.4} /> {t("lr_action_approve")}
          </button>
          <button
            onClick={() => onDecide(r, "rejected")}
            className="btn btn-ghost !h-9"
            data-testid={`reject-leave-${r._id}`}
          >
            <XIcon size={13} strokeWidth={2.4} /> {t("lr_action_reject")}
          </button>
        </div>
      )}
    </motion.div>
  );
}
